import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useKotchGame } from '../../hooks/useKotchGame';
import { usePeerConnection } from '../../hooks/usePeerConnection';
import { useKotchAudio } from '../../hooks/useKotchAudio';
import { KotchThemeProvider, useKotchTheme } from './KotchThemeContext';
import ThemeToggle from './ThemeToggle';
import GameSetup from './GameSetup';
import MultiplayerLobby from './MultiplayerLobby';
import PlayerTransition from './PlayerTransition';
import BattlefieldView from './BattlefieldView';
import FactionAbilitySelect from './FactionAbilitySelect';
import RoundResolution from './RoundResolution';
import BattleResults from './BattleResults';
import MatchResults from './MatchResults';
import type { GameConfig, GameState, LaneCommitment, PlayerId } from '../../types/kotch';
import '../../styles/Kotch.css';

// Import assets for preloading
import ArchersImg from './Archers.jpg';
import MithraImg from './Mithra.jpg';
import RidersImg from './Riders.jpg';
import SpearmenImg from './Spearmen.jpg';
import BackCoverImg from './KotchBackCover.jpg';
import BackCoverRegularImg from './KotchBackCoverRegular.jpg';
import VsAIImg from './vsAI.jpg';
import VsLocalImg from './vsLocal.jpg';
import VsOnlineImg from './vsOnline.jpg';
import KotchMusic from './Kotch.mp3';
import BattleMusic from './kotchBattleMusic.mp3';
import AmbienceSound from './ambience.mp3';

// CardArt imports for preloading
import HydariaArcherArt from './CardArt/hydaria_archer.png';
import HydariaRiderArt from './CardArt/hydaria_rider.png';
import HydariaSpearmanArt from './CardArt/hydaria_spearman.png';
import MigoraArcherArt from './CardArt/migora_archer.png';
import MigoraRiderArt from './CardArt/migora_rider.png';
import MigoraSpearmanArt from './CardArt/migora_spearman.png';
import MotraArcherArt from './CardArt/motra_archer.png';
import MotraRiderArt from './CardArt/motra_rider.png';
import MotraSpearmanArt from './CardArt/motra_spearman.png';
import RigataArcherArt from './CardArt/rigata_archer.png';
import RigataRiderArt from './CardArt/rigata_rider.png';
import RigataSpearmanArt from './CardArt/rigata_spearman.png';
import VeraArcherArt from './CardArt/vera_archer.png';
import VeraRiderArt from './CardArt/vera_rider.png';
import VeraSpearmanArt from './CardArt/vera_spearman.png';
import MithraKnightArt from './CardArt/migora_mithra.png';
import ReyazArt from './CardArt/migora_reyaz.png';
import BerjotArt from './CardArt/migora_berjot.png';
import HaflerArt from './CardArt/migora_hafler.png';
import AlonArt from './CardArt/migora_alon.png';
import EleiaArt from './CardArt/migora_eleia.png';
import LysiacArt from './CardArt/hydaria_lysiac.png';
import LysiacBattlemasterArt from './CardArt/hydaria_lysiac_battlemaster.png';
import RagosArt from './CardArt/hydaria_ragos.png';
import FarkionKingArt from './CardArt/motra_farkion_king.png';
import FarkionCowardArt from './CardArt/motra_farkion_coward.png';
import ArogArt from './CardArt/rigata_arog.png';
import RenedilArt from './CardArt/rigata_renedil.png';
import AverilArt from './CardArt/rigata_averil.png';
import QuitusArt from './CardArt/rigata_quitus.png';
import GimoraArt from './CardArt/rigata_gimora.png';

// All assets to preload
const CARD_ART_ASSETS = [
  HydariaArcherArt, HydariaRiderArt, HydariaSpearmanArt,
  MigoraArcherArt, MigoraRiderArt, MigoraSpearmanArt,
  MotraArcherArt, MotraRiderArt, MotraSpearmanArt,
  RigataArcherArt, RigataRiderArt, RigataSpearmanArt,
  VeraArcherArt, VeraRiderArt, VeraSpearmanArt,
  MithraKnightArt, ReyazArt, BerjotArt, HaflerArt, AlonArt, EleiaArt,
  LysiacArt, LysiacBattlemasterArt, RagosArt,
  FarkionKingArt, FarkionCowardArt,
  ArogArt, RenedilArt, AverilArt, QuitusArt, GimoraArt,
];

const IMAGE_ASSETS = [
  ArchersImg,
  MithraImg,
  RidersImg,
  SpearmenImg,
  BackCoverImg,
  BackCoverRegularImg,
  VsAIImg,
  VsLocalImg,
  VsOnlineImg,
  ...CARD_ART_ASSETS,
];

const AUDIO_ASSETS = [
  KotchMusic,
  BattleMusic,
  AmbienceSound,
];

type ViewMode = 'game' | 'lobby';

function KotchGameInner() {
  const [state, actions, helpers] = useKotchGame();
  const [viewMode, setViewMode] = useState<ViewMode>('game');
  const [playerName, setPlayerName] = useState('Commander');
  const [myPlayerId, setMyPlayerId] = useState<PlayerId | null>(null);

  // Audio management
  const { masterVolume, setMasterVolume, isMuted, toggleMute } = useKotchAudio(state.phase);

  // P2P connection handling
  const handleGameStart = useCallback((newState: GameState, playerId: PlayerId) => {
    console.log('[Kotch P2P] Guest received game start, playerId:', playerId);
    setMyPlayerId(playerId);
    setViewMode('game');
    // Guest applies the initial state from host
    actions.setState(newState);
  }, [actions]);

  const handleStateUpdate = useCallback((newState: GameState) => {
    // Guest receives state updates from host and applies them
    console.log('[Kotch P2P] Guest received state update, phase:', newState.phase);
    actions.setState(newState);
  }, [actions]);

  // Store isHost in a ref since we need it in callbacks before peer is defined
  const isHostRef = useRef(false);
  // Track when we need to send game start to guest (after state is updated)
  const pendingGameStartRef = useRef(false);

  const handleRemoteCommitment = useCallback((playerId: PlayerId, commitment: LaneCommitment) => {
    console.log('[Kotch P2P] Received commitment from player:', playerId, commitment);

    // Only HOST processes commitment messages (from guest)
    // Guest receives state updates from host which already include commitments
    if (!isHostRef.current) {
      console.log('[Kotch P2P] Ignoring commitment - not host');
      return;
    }

    // Host received guest's commitment - apply it and advance
    actions.aiMakeCommitment(commitment);
    setTimeout(() => {
      actions.confirmCommitment();
    }, 100);
  }, [actions]);

  const handleRemoteReady = useCallback((_playerId: PlayerId) => {
    // Handle remote player ready
    if (isHostRef.current) {
      actions.confirmPlayerReady();
    }
  }, [actions]);

  const peer = usePeerConnection({
    onGameStart: handleGameStart,
    onStateUpdate: handleStateUpdate,
    onCommitment: handleRemoteCommitment,
    onReady: handleRemoteReady,
  });

  // Sync isHostRef with peer.isHost
  useEffect(() => {
    isHostRef.current = peer.isHost;
  }, [peer.isHost]);

  // Auto-skip transitions: in single-player (vs AI), skip all transition screens
  // to keep focus on the battlefield board.
  const isSinglePlayer = Object.values(state.players).some(p => p.isAI);

  // Auto-start battle after battleStart phase
  useEffect(() => {
    if (state.phase === 'battleStart') {
      const delay = isSinglePlayer ? 0 : 1000;
      const timer = setTimeout(() => {
        actions.startBattle();
      }, delay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Auto-proceed for player transitions
  useEffect(() => {
    if (state.phase === 'playerTransition' && state.transitioningPlayer) {
      const player = state.players[state.transitioningPlayer];
      const isAI = player?.isAI;

      const shouldAutoConfirm = isAI || isSinglePlayer || (peer.isConnected && peer.isHost);

      if (shouldAutoConfirm) {
        const timer = setTimeout(() => {
          actions.confirmPlayerReady();
        }, 0);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.transitioningPlayer, peer.isConnected, peer.isHost]);

  // Auto-skip round end screen in single-player
  useEffect(() => {
    if (state.phase === 'roundEnd' && isSinglePlayer) {
      const timer = setTimeout(() => {
        actions.confirmRoundEnd();
      }, 1500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Auto-skip battle end screen in single-player
  useEffect(() => {
    if (state.phase === 'battleEnd' && isSinglePlayer) {
      const timer = setTimeout(() => {
        actions.confirmBattleEnd();
      }, 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Auto-select discard target for AI (Hydaria ability)
  useEffect(() => {
    if (state.phase === 'factionAbilitySelect' && state.pendingAbility) {
      const winnerId = state.pendingAbility.winnerId;
      const winner = state.players[winnerId];
      if (winner.isAI) {
        // AI picks randomly
        const eligible = state.pendingAbility.eligibleCardIds;
        const randomId = eligible[Math.floor(Math.random() * eligible.length)];
        const timer = setTimeout(() => {
          actions.selectDiscardTarget(randomId);
        }, 800);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.pendingAbility]);

  // Broadcast state to guest when host's state changes
  useEffect(() => {
    if (peer.isConnected && peer.isHost && state.phase !== 'setup') {
      peer.sendStateUpdate(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, peer.isConnected, peer.isHost, peer.sendStateUpdate]);

  // Send player info when connected
  useEffect(() => {
    if (peer.isConnected && playerName) {
      console.log('[Kotch P2P] Sending player info:', playerName);
      peer.sendPlayerInfo(playerName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peer.isConnected, playerName, peer.sendPlayerInfo]);

  const handleStartOnline = () => {
    setViewMode('lobby');
  };

  const handleCreateRoom = async () => {
    try {
      await peer.createRoom();
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  };

  const handleJoinRoom = async (code: string) => {
    try {
      await peer.joinRoom(code);
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  };

  const handleLobbyBack = () => {
    peer.disconnect();
    setViewMode('game');
  };

  const handleLobbyStartGame = () => {
    // Host starts the game
    const config: GameConfig = {
      player1Setup: {
        name: playerName || 'Host',
        isAI: false,
      },
      player2Setup: {
        name: peer.remoteName || 'Guest',
        isAI: false,
      },
      cardsPerBattle: 10,
      battlesToWin: 2,
    };

    // Mark that we need to send game start after state updates
    pendingGameStartRef.current = true;
    actions.startGame(config);
    setMyPlayerId(1);
    setViewMode('game');
  };

  // Handle online player confirming their commitment
  const handleOnlineCommit = useCallback(() => {
    if (!peer.isConnected || !myPlayerId) return;

    // Get the current player's commitment
    const commitment = myPlayerId === 1 ? state.player1Commitment : state.player2Commitment;
    console.log('[Kotch P2P] Sending commitment to peer:', myPlayerId, commitment);
    peer.sendCommitment(myPlayerId, commitment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peer.isConnected, myPlayerId, state.player1Commitment, state.player2Commitment, peer.sendCommitment]);

  // Send game start to guest once state has been updated (host only)
  useEffect(() => {
    if (pendingGameStartRef.current && peer.isConnected && state.phase === 'battleStart') {
      console.log('[Kotch P2P] Host sending game start to guest');
      peer.sendGameStart(state, 2);
      pendingGameStartRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, peer.isConnected, peer.sendGameStart]);

  // In single-player, all gameplay phases share the battlefield view
  // to prevent unmount/remount flicker between phases.
  const isGameplayPhase = state.phase !== 'setup' && state.phase !== 'matchEnd' && state.phase !== 'factionAbilitySelect';

  const renderContent = () => {
    // Show lobby when in online mode
    if (viewMode === 'lobby') {
      return (
        <MultiplayerLobby
          status={peer.status}
          roomCode={peer.roomCode}
          error={peer.error}
          isHost={peer.isHost}
          playerName={playerName}
          remoteName={peer.remoteName}
          onPlayerNameChange={setPlayerName}
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          onDisconnect={peer.disconnect}
          onStartGame={handleLobbyStartGame}
          onBack={handleLobbyBack}
        />
      );
    }

    // Single-player: keep the board mounted for all gameplay phases
    if (isSinglePlayer && isGameplayPhase) {
      return (
        <BattlefieldView
          state={state}
          actions={actions}
          helpers={helpers}
          myPlayerId={null}
          isOnline={false}
          isHost={false}
          onOnlineCommit={handleOnlineCommit}
        />
      );
    }

    // Normal game rendering (multiplayer / non-gameplay phases)
    switch (state.phase) {
      case 'setup':
        return (
          <GameSetup
            onStartGame={actions.startGame}
            onStartOnline={handleStartOnline}
          />
        );

      case 'battleStart':
        return (
          <motion.div
            className="kotch-transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <h2>Battle {state.match.currentBattle.battleNumber}</h2>
            <p>Drawing cards...</p>
          </motion.div>
        );

      case 'playerTransition':
        if (peer.isConnected) return null;

        if (state.transitioningPlayer) {
          const player = state.players[state.transitioningPlayer];
          const isAttacker = state.transitioningPlayer === state.currentAttackerId;

          return (
            <PlayerTransition
              player={player}
              isAttacker={isAttacker}
              battleNumber={state.match.currentBattle.battleNumber}
              roundNumber={state.match.currentBattle.roundNumber}
              onReady={actions.confirmPlayerReady}
            />
          );
        }
        return null;

      case 'commitment':
      case 'reveal':
      case 'resolution':
        return (
          <BattlefieldView
            state={state}
            actions={actions}
            helpers={helpers}
            myPlayerId={peer.isConnected ? myPlayerId : null}
            isOnline={peer.isConnected}
            isHost={peer.isHost}
            onOnlineCommit={handleOnlineCommit}
          />
        );

      case 'factionAbilitySelect':
        if (state.pendingAbility) {
          return (
            <FactionAbilitySelect
              pendingAbility={state.pendingAbility}
              players={state.players}
              onSelectTarget={actions.selectDiscardTarget}
            />
          );
        }
        return null;

      case 'roundEnd':
        if (state.currentRoundResult) {
          return (
            <RoundResolution
              result={state.currentRoundResult}
              player1Name={state.players[1].name}
              player2Name={state.players[2].name}
              onContinue={actions.confirmRoundEnd}
              isOnline={peer.isConnected}
              isHost={peer.isHost}
            />
          );
        }
        return null;

      case 'battleEnd':
        return (
          <BattleResults
            battle={state.match.currentBattle}
            match={state.match}
            player1Name={state.players[1].name}
            player2Name={state.players[2].name}
            onContinue={actions.confirmBattleEnd}
            isOnline={peer.isConnected}
            isHost={peer.isHost}
          />
        );

      case 'matchEnd':
        return (
          <MatchResults
            state={state}
            onPlayAgain={() => {
              peer.disconnect();
              actions.resetGame();
            }}
            onExit={() => window.location.href = '/creative'}
          />
        );

      default:
        return null;
    }
  };

  const { theme } = useKotchTheme();

  return (
    <div className={`kotch-game kotch-theme-${theme}`}>
      <header className="kotch-header">
        <Link to="/creative" className="kotch-back-link">
          ← Back
        </Link>
        <h1 className="kotch-title">Kotch</h1>
        <div className="kotch-header-right">
          {/* Volume Controls */}
          <div className="kotch-volume-controls">
            <button
              className="kotch-volume-btn"
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : masterVolume > 0.5 ? '🔊' : masterVolume > 0 ? '🔉' : '🔈'}
            </button>
            <input
              type="range"
              className="kotch-volume-slider"
              min={0}
              max={100}
              value={isMuted ? 0 : masterVolume * 100}
              onChange={(e) => {
                const newVolume = Number(e.target.value) / 100;
                setMasterVolume(newVolume);
                if (newVolume > 0 && isMuted) {
                  toggleMute();
                }
              }}
              title={`Volume: ${Math.round(masterVolume * 100)}%`}
            />
          </div>
          {peer.isConnected && (
            <span className="kotch-online-indicator">● Online</span>
          )}
          <ThemeToggle />
        </div>
      </header>

      <main className="kotch-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode + (isSinglePlayer && isGameplayPhase ? 'gameplay' : state.phase + (state.transitioningPlayer || ''))}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Global Message */}
      <AnimatePresence>
        {state.message && (
          <motion.div
            className="kotch-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {state.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Lost Warning */}
      <AnimatePresence>
        {peer.connectionLost && (
          <motion.div
            className="kotch-connection-lost"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="kotch-connection-lost-content">
              <div className="kotch-connection-lost-icon">⚠</div>
              <h3>Connection Lost</h3>
              <p>Unable to reach your opponent. The connection may have dropped.</p>
              <div className="kotch-connection-lost-actions">
                <button
                  className="kotch-button kotch-button-secondary"
                  onClick={() => {
                    peer.disconnect();
                    actions.resetGame();
                    setViewMode('game');
                  }}
                >
                  Return to Menu
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Loading screen component
function LoadingScreen({ progress }: { progress: number }) {
  return (
    <div className="kotch-loading-screen">
      <div className="kotch-loading-content">
        <h1 className="kotch-loading-title">Kotch</h1>
        <div className="kotch-loading-subtitle">Loading assets...</div>
        <div className="kotch-loading-bar-container">
          <motion.div
            className="kotch-loading-bar"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <div className="kotch-loading-percent">{Math.round(progress)}%</div>
      </div>
    </div>
  );
}

// Asset preloader hook
function useAssetPreloader() {
  const [progress, setProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    const totalAssets = IMAGE_ASSETS.length + AUDIO_ASSETS.length;
    let loadedCount = 0;

    const updateProgress = () => {
      loadedCount++;
      if (mounted) {
        const newProgress = (loadedCount / totalAssets) * 100;
        setProgress(newProgress);
        if (loadedCount === totalAssets) {
          // Small delay for smooth transition
          setTimeout(() => {
            if (mounted) setIsLoaded(true);
          }, 300);
        }
      }
    };

    // Preload images
    IMAGE_ASSETS.forEach((src) => {
      const img = new Image();
      img.onload = updateProgress;
      img.onerror = updateProgress; // Count errors as loaded to not block
      img.src = src;
    });

    // Preload audio (just fetch the headers/start loading)
    AUDIO_ASSETS.forEach((src) => {
      const audio = new Audio();
      audio.preload = 'auto';

      const handleLoaded = () => {
        audio.removeEventListener('canplaythrough', handleLoaded);
        audio.removeEventListener('error', handleLoaded);
        updateProgress();
      };

      audio.addEventListener('canplaythrough', handleLoaded);
      audio.addEventListener('error', handleLoaded); // Count errors as loaded
      audio.src = src;
    });

    return () => {
      mounted = false;
    };
  }, []);

  return { progress, isLoaded };
}

// Wrapper component with theme provider and loading screen
export default function KotchGame() {
  const { progress, isLoaded } = useAssetPreloader();

  return (
    <KotchThemeProvider>
      <AnimatePresence mode="wait">
        {!isLoaded ? (
          <motion.div
            key="loading"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingScreen progress={progress} />
          </motion.div>
        ) : (
          <motion.div
            key="game"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <KotchGameInner />
          </motion.div>
        )}
      </AnimatePresence>
    </KotchThemeProvider>
  );
}
